/**
 * Store Visit Hook
 * 
 * Manages store visit lifecycle (start, pause, complete) with state management
 */

import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import type { StoreVisit, VisitProgress, VisitSignature, CompletedTask } from '@/types/store-first';

interface UseStoreVisitReturn {
  visitState: StoreVisit | null;
  isLoading: boolean;
  error: string | null;
  startVisit: () => Promise<void>;
  pauseVisit: (progress: VisitProgress) => Promise<void>;
  completeVisit: (signature: VisitSignature, completedTasks: CompletedTask[]) => Promise<void>;
  refreshVisit: () => Promise<void>;
}

export function useStoreVisit(storeId: number, cycleId?: number): UseStoreVisitReturn {
  const [visitState, setVisitState] = useState<StoreVisit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startVisit = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiFetch(`/api/mobile/store-visit/${storeId}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cycleId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to start visit: ${response.status}`);
      }

      const data = await response.json();
      setVisitState(data.visit);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start visit';
      setError(errorMessage);
      console.error('[useStoreVisit] Error starting visit:', err);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, cycleId]);

  const pauseVisit = useCallback(async (progress: VisitProgress) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiFetch(`/api/mobile/store-visit/${storeId}/pause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(progress),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to pause visit: ${response.status}`);
      }

      // Update local state
      setVisitState(prev => prev ? {
        ...prev,
        visitStatus: 'paused',
        visitPausedAt: new Date(),
      } : null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to pause visit';
      setError(errorMessage);
      console.error('[useStoreVisit] Error pausing visit:', err);
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  const completeVisit = useCallback(async (
    signature: VisitSignature,
    completedTasks: CompletedTask[]
  ) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiFetch(`/api/mobile/store-visit/${storeId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signature, completedTasks }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to complete visit: ${response.status}`);
      }

      const data = await response.json();
      
      // Update local state
      setVisitState(prev => prev ? {
        ...prev,
        visitStatus: 'completed',
        visitCompletedAt: new Date(),
        signature,
      } : null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete visit';
      setError(errorMessage);
      console.error('[useStoreVisit] Error completing visit:', err);
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  const refreshVisit = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiFetch(`/api/mobile/store-visit/${storeId}`);

      if (!response.ok) {
        if (response.status === 404) {
          // Visit doesn't exist yet - that's okay
          setVisitState(null);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch visit: ${response.status}`);
      }

      const data = await response.json();
      setVisitState(data.visit);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh visit';
      setError(errorMessage);
      console.error('[useStoreVisit] Error refreshing visit:', err);
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  return {
    visitState,
    isLoading,
    error,
    startVisit,
    pauseVisit,
    completeVisit,
    refreshVisit,
  };
}

